import { Component, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LoaderService } from "../../../core/services/loader.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TableModule } from "primeng/table";
import { BcledgerService } from "../../../core/services/bcledger.service";
import { DashboardPocService } from "../../../core/services/dashboard-poc.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { AddMultiLedgerAssociationRequestDto } from "../../../core/models/multi-ledger/addMultiLedgerAssociationRequestDto";
import { GetMultiLedgerAssociations } from "../../../core/models/multi-ledger/getMultiLedgerAssociations";
import { Employee } from "../../../core/models/profile/employee";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";

@UntilDestroy()
@Component({
  selector: "jni-multi-ledger-association",
  templateUrl: "./multi-ledger-association.component.html",
  styleUrls: ["./multi-ledger-association.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    SelectModule,
    CardModule,
    ButtonModule,
  ],
})
export class MultiLedgerAssociationComponent implements OnInit {
  bcList: Employee[] = [];
  availableAssociatedBcList: Employee[] = [];
  associations: GetMultiLedgerAssociations[] = [];
  selectedAssociations: GetMultiLedgerAssociations[] = [];
  selectedBc = "";
  selectedAssociatedBc = "";

  constructor(
    private _bcledgerService: BcledgerService,
    private _loader: LoaderService,
    private _dashboardPocService: DashboardPocService,
    private _toastr: JniToastrService
  ) {}

  ngOnInit(): void {
    this.getBcUser();
  }

  getBcUser() {
    this._loader.setLoaderState(true);
    this._bcledgerService
      .getAllEmployees()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loader.setLoaderState(false);
          this.bcList = res.data.map(com => ({
            ...com,
            fullName: `${com.firstName} ${com.lastName} (${com.personnelNumber})`,
          }));
        },
        error: () => {
          this._loader.setLoaderState(false);
        },
      });
  }

  getMultiLedgerAssociations() {
    if (!this.selectedBc) {
      return;
    }
    this._loader.setLoaderState(true);
    this._dashboardPocService
      .getMultiLedgerAssociations(this.selectedBc)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loader.setLoaderState(false);
          this.associations = res.data ?? [];
          this.getAvailableAssociatedBc();
        },
        error: () => {
          this._loader.setLoaderState(false);
        },
      });
  }

  getAvailableAssociatedBc() {
    const associatedPersonnelNumbers = this.associations.map(
      x => x.associatedBCPersonnelNumber
    );

    this.availableAssociatedBcList = this.bcList.filter(
      x => !associatedPersonnelNumbers.includes(x.personnelNumber ?? "")
    );
  }

  addMultiLedgerAssociation() {
    if (!this.selectedBc) {
      this._toastr.error(`You Have To Select A Branding Consultant`, `Error`);
      return;
    }

    if (!this.selectedAssociatedBc) {
      return;
    }

    const request = {
      associatedBCPersonnelNumber: this.selectedAssociatedBc,
      bcPersonnelNumber: this.selectedBc,
    } as AddMultiLedgerAssociationRequestDto;

    this._loader.setLoaderState(true);
    this._dashboardPocService.addMultiLedgerAssociation(request).subscribe({
      next: res => {
        this._loader.setLoaderState(false);
        if (res.status.toLowerCase() == "success") {
          this._toastr.success(
            res.message ?? `Associations Added Successfully`,
            `Success`
          );
        } else {
          this._toastr.error(
            res.message ?? `Something Went Wrong While Adding Associations`,
            `Error`
          );
        }

        this.selectedAssociatedBc = "";
        this.getMultiLedgerAssociations();
      },
      error: () => {
        this._loader.setLoaderState(false);
        this.selectedAssociatedBc = "";
        this._toastr.error(
          `Something Went Wrong While Adding Associations`,
          `Error`
        );
      },
    });
  }

  deleteMultiLedgerAssociation() {
    const idList = this.selectedAssociations.map(x => x.id);
    this._loader.setLoaderState(true);
    this._dashboardPocService.deleteMultiLedgerAssociation(idList).subscribe({
      next: res => {
        this._loader.setLoaderState(false);
        if (res.status.toLowerCase() == "success") {
          this._toastr.success(
            res.message ?? `Associations Deleted Successfully`,
            `Success`
          );
        } else {
          this._toastr.error(
            res.message ?? `Something Went Wrong While Deleting Associations`,
            `Error`
          );
        }

        this.selectedAssociations = [];
        this.getMultiLedgerAssociations();
      },
      error: () => {
        this._loader.setLoaderState(false);
      },
    });
  }
}
